import { Maximize2, Minimize2, X } from 'lucide-react';
import { useState } from 'react';
import type { KeyboardEvent, PointerEvent, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { useFocusReturn } from '../lib/focus.ts';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from '../primitives/sheet.tsx';
import { IconButton } from './IconButton.tsx';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What this drawer shows. Required: it is the accessible name. */
  title: ReactNode;
  /** Above the title: a status, a kind. */
  eyebrow?: ReactNode;
  /** One line under the title: where the thing lives. */
  description?: ReactNode;
  /** Header controls, left of the close button (compact buttons). */
  actions?: ReactNode;
  /**
   * How the header is laid out. 'side' (default): eyebrow, title and description in a column,
   * the actions beside them. 'rows': a grid of rows, so the title gets the full width: the
   * eyebrow with the actions and close, then the title with `titleAside` at its right end,
   * then the description on one line.
   */
  layout?: 'side' | 'rows';
  /** In the 'rows' layout: the primary action, at the right end of the title row. */
  titleAside?: ReactNode;
  children?: ReactNode;
  /** Pinned under the body. */
  footer?: ReactNode;
  /** Max width in px: 480 to inspect, 640 to edit or review (default). Full width on a phone. */
  width?: number;
  /**
   * Remembers the width a person drags this drawer to (its left edge), under this key in the
   * browser; the header also gets Expand, which slides it out to nearly the full window. On by
   * default under 'drawer'; pass null to keep a drawer at its fixed width.
   */
  resizeKey?: string | null;
  className?: string;
  bodyClassName?: string;
  /** Keys handled while the drawer has focus (for example j and k to move between records). */
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  /**
   * Where focus goes on close. By default it returns to what had it before the drawer opened;
   * call preventDefault() and focus something yourself when that element may be gone.
   */
  onCloseAutoFocus?: (e: Event) => void;
}

/**
 * A side sheet for the detail of one record, on the shadcn Sheet (components.md, Overlays): a
 * floating raised panel with the 12 px overlay radius on a desktop, full bleed on a phone.
 * Slides in from the right over 320 ms on the emphasized curve and out in 120 ms; under
 * reduced motion it appears in place. Esc closes it and focus returns to the row.
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  eyebrow,
  description,
  actions,
  layout = 'side',
  titleAside,
  children,
  footer,
  width = 640,
  resizeKey = 'drawer',
  className,
  bodyClassName,
  onKeyDown,
  onCloseAutoFocus,
}: DrawerProps) {
  // Drawers open from a URL parameter, so Radix has no trigger to give focus back to: keep
  // the element that had focus when it opened (WCAG 2.4.3), and find it again by its data
  // attributes, id or label when its row re-rendered meanwhile.
  const focusReturn = useFocusReturn(open);
  const size = useDrawerSize(resizeKey, width);
  const expand = size.enabled && (
    <IconButton
      label={size.expanded ? 'Restore width' : 'Expand'}
      icon={size.expanded ? <Minimize2 /> : <Maximize2 />}
      size="sm"
      className="hidden sm:inline-flex"
      onClick={size.toggleExpanded}
    />
  );
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        aria-modal="true"
        tabIndex={-1}
        onOpenAutoFocus={(e) => {
          focusReturn.onOpenAutoFocus(e);
          // Focus the panel itself, not the first button, so no tooltip pops open.
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
        onCloseAutoFocus={(e) => {
          onCloseAutoFocus?.(e);
          focusReturn.onCloseAutoFocus(e);
        }}
        onKeyDown={onKeyDown}
        style={{ maxWidth: size.maxWidth, transition: size.dragging ? 'none' : 'max-width var(--duration-slow) var(--easing-standard)' }}
        className={cn('w-full sm:max-w-none', className)}
      >
        {size.enabled && (
          // The left edge: drag to resize (double click restores), or focus it and use the arrow keys.
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the panel"
            aria-valuemin={DRAWER_MIN}
            aria-valuemax={size.max}
            aria-valuenow={Math.round(size.current)}
            tabIndex={0}
            onPointerDown={size.onPointerDown}
            onDoubleClick={size.reset}
            onKeyDown={size.onKeyDown}
            className="group/resize absolute inset-y-0 left-0 z-(--z-sticky) hidden w-2 -translate-x-1/2 cursor-col-resize items-center justify-center outline-none sm:flex"
          >
            <span aria-hidden className="h-full w-px bg-transparent transition-colors duration-(--duration-fast) group-hover/resize:bg-accent group-focus-visible/resize:bg-accent" />
            <span aria-hidden className="absolute h-8 w-1 rounded-pill bg-border-strong opacity-0 transition-opacity duration-(--duration-fast) group-hover/resize:opacity-100 group-focus-visible/resize:opacity-100" />
          </div>
        )}
        {/* A div, not a header: inside the dialog a <header> was a second banner landmark. */}
        {layout === 'rows' ? (
          <div className="flex shrink-0 flex-col px-6 pb-6 pt-4">
            <div className="-mr-2 flex h-control-sm items-center gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-2 text-caption text-fg-muted">{eyebrow}</div>
              <div className="flex shrink-0 items-center gap-1">
                {actions}
                {expand}
                <SheetClose asChild>
                  <IconButton label="Close" icon={<X />} size="sm" shortcut={['esc']} />
                </SheetClose>
              </div>
            </div>
            <div className="mt-3 flex items-start gap-3">
              <SheetTitle className="line-clamp-2 min-w-0 flex-1 pr-0 text-balance">{title}</SheetTitle>
              {/* The control is 28 px, the title's line 28 px: both share the first line's centre. */}
              {titleAside && <div className="flex shrink-0 items-center gap-2">{titleAside}</div>}
            </div>
            {description ? (
              <SheetDescription asChild>
                <div className="mt-4 min-w-0">{description}</div>
              </SheetDescription>
            ) : (
              <SheetDescription className="sr-only">{typeof title === 'string' ? title : 'Details'}</SheetDescription>
            )}
          </div>
        ) : (
          <div className="flex shrink-0 items-start gap-3 border-b border-border-subtle px-5 pb-3.5 pt-4">
            <div className="min-w-0 flex-1">
              {eyebrow && <div className="mb-1.5 flex items-center gap-2 text-caption text-fg-muted">{eyebrow}</div>}
              <SheetTitle className="pr-0">{title}</SheetTitle>
              {description ? (
                <SheetDescription asChild>
                  <div className="mt-1">{description}</div>
                </SheetDescription>
              ) : (
                <SheetDescription className="sr-only">{typeof title === 'string' ? title : 'Details'}</SheetDescription>
              )}
            </div>
            <div className="-mr-2 -mt-0.5 flex shrink-0 items-center gap-1">
              {actions}
              {expand}
              <SheetClose asChild>
                <IconButton label="Close" icon={<X />} size="sm" shortcut={['esc']} />
              </SheetClose>
            </div>
          </div>
        )}
        <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain', bodyClassName)}>{children}</div>
        {/* data-drawer-footer: toasts rise above it, so they never cover its primary action. */}
        {footer && <div data-drawer-footer="" className="flex shrink-0 items-center gap-2 border-t border-border-subtle bg-sunken/50 px-5 py-3">{footer}</div>}
      </SheetContent>
    </Sheet>
  );
}

/** The narrowest a drawer may be dragged to, and how much of the window an expanded one leaves showing. */
export const DRAWER_MIN = 400;
const EXPANDED_GAP = 72;

function readWidth(key: string): number | null {
  try {
    const v = Number(localStorage.getItem(`orgus.${key}.width`));
    return Number.isFinite(v) && v >= DRAWER_MIN ? v : null;
  } catch {
    return null;
  }
}

/** The width state of a drawer: its own default, the width a person dragged it to, or expanded. */
function useDrawerSize(key: string | null, base: number) {
  const enabled = key !== null;
  const [custom, setCustom] = useState<number | null>(() => (key ? readWidth(key) : null));
  const [expanded, setExpanded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const max = typeof window === 'undefined' ? 1600 : Math.max(DRAWER_MIN, window.innerWidth - EXPANDED_GAP);
  const current = expanded ? max : Math.min(max, custom ?? base);
  const save = (w: number | null) => {
    setCustom(w);
    if (!key) return;
    try {
      if (w == null) localStorage.removeItem(`orgus.${key}.width`);
      else localStorage.setItem(`orgus.${key}.width`, String(Math.round(w)));
    } catch {
      // Storage blocked: the width lasts while the drawer is open.
    }
  };
  const clamp = (w: number) => Math.min(max, Math.max(DRAWER_MIN, w));
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    setDragging(true);
    setExpanded(false);
    // The drawer's right edge stays where it is: its width is that edge minus the pointer.
    const right = (el.parentElement?.getBoundingClientRect().right ?? window.innerWidth);
    const move = (ev: globalThis.PointerEvent) => setCustom(clamp(right - ev.clientX));
    const up = (ev: globalThis.PointerEvent) => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      setDragging(false);
      save(clamp(right - ev.clientX));
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 96 : 32;
    if (e.key === 'ArrowLeft') save(clamp(current + step));
    else if (e.key === 'ArrowRight') save(clamp(current - step));
    else if (e.key === 'Home') save(DRAWER_MIN);
    else if (e.key === 'End') save(max);
    else return;
    e.preventDefault();
    setExpanded(false);
  };
  return {
    enabled,
    expanded,
    dragging,
    max,
    current,
    maxWidth: enabled ? (expanded ? `calc(100vw - ${EXPANDED_GAP}px)` : current) : base,
    toggleExpanded: () => setExpanded((x) => !x),
    reset: () => {
      setExpanded(false);
      save(null);
    },
    onPointerDown,
    onKeyDown,
  };
}
